import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

import { Lock } from '@crehana/icons/dist/fill';

import {
  Calendar,
  Clock,
  Star,
  CrehanaLiveKids,
  CrehanaLiveDay,
} from '@crehana/icons';

import {
  PrimaryButton,
  Container,
  LazyImageResponsive,
  LazyImage,
  colors,
} from '@crehana/ui';

import { LiveTargetEnum } from 'Jsx/Types/Graphql/globals';
import { useMe } from 'Jsx/ContextApis/AuthContext';

import Speakers from 'Jsx/CrehanaLive/components/Speakers';
import { KIDS_SLUG } from 'Jsx/CrehanaLive/constants';
import { LiveNode } from 'Jsx/CrehanaLive/graphql/types/LiveNode';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';

import OnLiveTag from '../components/OnLiveTag';
import { formatDate } from '../utils';
import { ConfigurationType } from '../types';

const Wrapper = styled.main`
  height: auto;
  ${props => props.theme.breakpoints.up('md')} {
    height: 480px;
  }
`;

const Inner = styled.section`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up('md')} {
    max-width: 485px;
  }
`;

const MobileImage = styled.div<{ src: string }>`
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: top;
  min-height: 160px;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    min-height: 400px;
  }
`;

interface HeroProps {
  live?: LiveNode | null;
  configuration?: ConfigurationType;
}

const Hero: React.FC<HeroProps> = ({ live: liveProp, configuration }) => {
  const { t } = useCrehanaLiveTranslations();
  const { me, loading: loadingMe } = useMe();

  if (!liveProp) {
    return (
      <Wrapper className="relative sm:py-16 sm:py-0">
        <div className="inset-0 block sm:absolute min-h-160">
          <LazyImageResponsive
            alt="Crehana Live!"
            className="w-full h-full"
            sources={{
              default:
                configuration?.livesBannerMobile ||
                '/static/img/crehana-lives/banner-320.png',
              sm:
                configuration?.livesBannerTablet ||
                '/static/img/crehana-lives/banner-1024.png',
              md:
                configuration?.livesBannerTablet ||
                '/static/img/crehana-lives/banner-1440.png',
              lg:
                configuration?.livesBannerDesktop ||
                '/static/img/crehana-lives/banner-1920.png',
            }}
            imgClassName="object-cover object-center h-auto md:h-full"
            // @ts-ignore
            css={css`
              padding-bottom: 50%;
              &:before {
                content: '';
                z-index: 1;
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: linear-gradient(
                  rgb(24, 27, 50) 0%,
                  rgba(24, 27, 50, 0) 100%
                );
                transform: rotate(180deg);
              }
              ${props => props.theme.breakpoints.up('sm')} {
                padding-bottom: 0;
                &:before {
                  content: none;
                }
              }
            `}
            isTransparent
          />
        </div>
        <Container className="relative flex flex-row items-center w-full h-full mt-16 sm:mt-0">
          <Inner>
            <h1 className="block mt-24 whitespace-pre-line font-h3 text-white">
              {configuration?.livesTitle || t('CREHANA_LEARN')}
            </h1>
            {configuration?.livesDescription && (
              <p className="text-white font-body2 my-16">
                {configuration?.livesDescription}
              </p>
            )}
            <PrimaryButton
              className="w-auto mt-24 sm:w-auto py-0 px-12"
              onClick={() => {
                const liveListSection = document.querySelector(
                  '.categories-and-lives',
                );

                if (liveListSection) {
                  const { top } = liveListSection.getBoundingClientRect();

                  window.scrollBy({ top: top - 24, behavior: 'smooth' });
                }
              }}
              label={configuration?.livesBannerCta || t('SEE_TRANSMISSIONS')}
              darkMode
            />
          </Inner>
        </Container>
      </Wrapper>
    );
  }

  const live = liveProp;
  const onLive = live.isLive;
  const isKidsLive = live.liveCategory?.slug === KIDS_SLUG;
  const isPremiumLive = live.targetEnum === LiveTargetEnum.TARGET_PREMIUM;
  // eslint-disable-next-line no-nested-ternary
  const notLiveTag = live.tags?.includes('live-day-adobe') ? (
    <CrehanaLiveDay size={24} color={colors.white} className="mb-8" />
  ) : isKidsLive ? (
    <CrehanaLiveKids size={24} />
  ) : (
    <p className="font-label text-base-lighter">{t('NEXT_LIVE')}</p>
  );
  const LiveTag = onLive ? (
    <div className="flex items-center">
      <OnLiveTag light />
    </div>
  ) : (
    notLiveTag
  );

  return (
    <Wrapper className="relative md:py-16 bg-base-dark">
      <div className="absolute inset-0 hidden md:block">
        <LazyImage
          src={live.bannerPcImageUrl || ''}
          alt={live.title}
          css={`
            width: 741px;
            height: 480px;
            right: 0;
            top: 0;
            bottom: 0;
            position: absolute;
            @media (min-width: 1450px) {
              width: 920px;
            }
          `}
          imgClassName="object-cover object-center"
          backgroundImage={`radial-gradient(100% 610.32% at 100% 40.83%, rgba(17, 20, 40, 0) 13.06%, rgba(17, 20, 40, 0.53366) 38.15%, ${colors.baseDark} 47.3%);`}
          isTransparent
        />
      </div>
      <div
        css={`
          background: radial-gradient(
            79.83% 487.23% at 79.83% 42.29%,
            rgba(17, 22, 40, 0) 0%,
            rgba(17, 20, 40, 0.53366) 31.98%,
            ${colors.baseDark} 38.33%
          );
          @media (min-width: 1450px) {
            background: radial-gradient(
              100% 610.32% at 100% 40.83%,
              rgba(17, 20, 40, 0) 13.06%,
              rgba(17, 20, 40, 0.53366) 38.15%,
              ${colors.baseDark} 47.3%
            );
          }
        `}
        className="absolute inset-0 hidden md:block"
      />
      <MobileImage
        className="relative block w-full md:hidden"
        src={live.bannerMobileImageUrl || ''}
      >
        <div
          css={`
            background: linear-gradient(
              180deg,
              ${colors.baseDark} 0%,
              rgba(24, 27, 50, 0) 50%
            );
            transform: rotate(180deg);
          `}
          className="absolute inset-0 block md:hidden"
        />
      </MobileImage>
      <Container className="relative flex flex-row items-center w-full h-full mt-16 sm:mt-24 md:mt-0 z-3">
        <Inner>
          <div className="flex items-center">
            {LiveTag}
            {!loadingMe && isPremiumLive && (
              <div className="flex items-center font-label text-secondary-light">
                <div className="h-16 w-1 mx-12 bg-base-lighter" />
                {me?.isPremiumMember ? (
                  <Star size={16} ignoreColor className="fill-current mr-8" />
                ) : (
                  <Lock size={16} ignoreColor className="fill-current mr-8" />
                )}
                {t('PREMIUM_EXCLUSIVE')}
              </div>
            )}
          </div>

          <h1 className="font-h4 text-white mt-12">{live.title}</h1>
          <Speakers
            presenter={live.presenter}
            presenters={live.presenters}
            className="py-16"
          />
          {!onLive && (
            <Fragment>
              <p className="font-body4 text-gray-dark block">
                {live.description}
              </p>
              <div className="flex items-center font-subtitle4 text-base-lighter mt-16">
                <Calendar size={20} ignoreColor className="fill-current mr-8" />
                {formatDate(live.startLive).dayAndMonth}
                <Clock
                  size={20}
                  ignoreColor
                  className="fill-current ml-16 mr-8"
                />
                {formatDate(live.startLive).minutesAndHours}
              </div>
            </Fragment>
          )}
          <div className="mt-24">
            <Link
              to={{
                pathname: live.liveDetailUrl!,
                state: {
                  liveDetail: live,
                },
              }}
            >
              <PrimaryButton
                className="w-full sm:w-auto"
                type="button"
                label={
                  onLive && (!isPremiumLive || me?.isPremiumMember)
                    ? t('GO_TRANSMISSION')
                    : t('SEE_INFO')
                }
                isDark
              />
            </Link>
          </div>
        </Inner>
      </Container>
    </Wrapper>
  );
};

export default Hero;
